import { Box } from '@factorialco/deprecated-design-system'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { INCIDENCE_TYPES } from 'modules/payroll_bundle/lib/Incidences/Types'
import Available from 'modules/payroll_integrations_datev_lauds/components/Incidence/Banners/Available'
import Blocked from 'modules/payroll_integrations_datev_lauds/components/Incidence/Banners/Blocked'
import Done from 'modules/payroll_integrations_datev_lauds/components/Incidence/Banners/Done'
import PartialSync from 'modules/payroll_integrations_datev_lauds/components/Incidence/Banners/PartialSync'
import Pending from 'modules/payroll_integrations_datev_lauds/components/Incidence/Banners/Pending'
import Unavailable from 'modules/payroll_integrations_datev_lauds/components/Incidence/Hiring/Unavailable'
import type { Sync } from 'modules/payroll_integrations_datev_lauds/lib/IncidenceSync/store'
import { Status } from 'modules/payroll_integrations_datev_lauds/lib/IncidenceSync/store'

interface Props {
  sync: Sync
  syncing: boolean
  onSync: () => void
  onStatusCheck: () => void
  checkingStatus: boolean
  pendingState: boolean
  setPendingState: (value: boolean) => void
  incidenceType: keyof typeof INCIDENCE_TYPES
}

const Hiring = ({
  sync,
  syncing,
  onSync,
  onStatusCheck,
  checkingStatus,
  pendingState,
  setPendingState,
  incidenceType,
}: Props) => {
  const {
    access: { id: accessId },
    company: { id: companyId },
  } = useCurrentSession()

  const syncContent = () => {
    switch (sync.status) {
      case Status.Available:
        return (
          <Available
            onSync={onSync}
            syncing={syncing}
            title={i18n.t('payroll_integrations.incidences.datev_lauds.hiring.available.title')}
            description={i18n.t(
              'payroll_integrations.incidences.datev_lauds.hiring.available.message'
            )}
            submitButton={i18n.t(
              'payroll_integrations.incidences.datev_lauds.hiring.available.submit'
            )}
          />
        )
      case Status.Done:
        setPendingState(false)
        ;(track(trackingEvents.payrollIntegrationsDatevLauds.datevLaudsEmployeeCreated),
          {
            company_id: companyId,
            access_id: accessId,
          })

        return sync?.unsync_fields && sync?.unsync_fields.length > 0 ? (
          <PartialSync unsync_fields={sync?.unsync_fields} incidenceType={incidenceType} />
        ) : (
          <Done
            title={i18n.t('payroll_integrations.incidences.datev_lauds.hiring.done.title')}
            description={i18n.t('payroll_integrations.incidences.datev_lauds.hiring.done.message')}
          />
        )
      case Status.Pending:
        return (
          <Pending
            checkingStatus={checkingStatus}
            onStatusCheck={onStatusCheck}
            pendingState={pendingState}
          />
        )
      case Status.Blocked:
        setPendingState(false)
        return (
          <Blocked
            sync={sync}
            onSync={onSync}
            syncing={syncing}
            submitButton={i18n.t('payroll_integrations.incidences.datev_lauds.hiring.error.retry')}
          />
        )
      case Status.Unavailable:
        return <Unavailable sync={sync} />
      default:
        throw new Error(`Rendering unknown status ${sync.status}`)
    }
  }

  const background = () => {
    switch (sync.status) {
      case Status.Available:
        return 'darker080'
      case Status.Unavailable:
        return 'champagne200'
      case Status.Done:
        return sync?.unsync_fields && sync?.unsync_fields.length > 0 ? 'tangerine200' : 'lime200'
      case Status.Pending:
        return 'viridian100'
      case Status.Blocked:
        return 'radicalRed200'
      default:
        throw new Error(`Rendering unknown status ${sync.status}`)
    }
  }

  if (
    sync.status === Status.Unavailable &&
    sync?.unavailable_fields?.every((item) =>
      ['missing_salary_code', 'missing_legal_entity_mapping'].includes(item)
    )
  )
    return

  return (
    <Box
      background={background()}
      paddingX="s24"
      paddingY="s16"
      borderRadius={{
        all: 'abs008',
      }}
      gap="s16"
    >
      {syncContent()}
    </Box>
  )
}

export default Hiring
